import { FC } from 'react'
import useDateFormat from 'hooks/useDateFormat'
import { DateFormat, DateTimeInput, formatDate, formatTime, TimeFormat, toDate } from 'utils/dateFormat'

const CLOCK_TIME = /^(\d{1,2}):(\d{2})(?::\d{2})?(?:\s*[AaPp][Mm])?$/

interface DateTimeProps {
    /** Full timestamp. Supplies both the date and the time. */
    timestamp?: DateTimeInput
    /** Date used when a full timestamp is not passed. */
    date?: DateTimeInput
    /** Time used when a full timestamp is not passed. Omit to show the date only. */
    time?: DateTimeInput
    /** Hide the time on small devices (below 768px). */
    hideTimeOnSm?: boolean
    /** Hide the time on medium devices (768px up to 1024px). */
    hideTimeOnMd?: boolean
    separator?: string
    className?: string
}

const timeVisibilityClass = (hideTimeOnSm: boolean, hideTimeOnMd: boolean) => {
    if (hideTimeOnSm && hideTimeOnMd) return '!hidden lg:!inline'
    if (hideTimeOnSm) return '!hidden md:!inline'
    if (hideTimeOnMd) return 'md:!hidden lg:!inline'
    return undefined
}

const dateTextFrom = (value: DateTimeInput, format: DateFormat) => {
    const parsed = toDate(value)
    if (parsed) return formatDate(parsed, format)
    return typeof value === 'string' ? value : ''
}

const timeTextFrom = (value: DateTimeInput, format: TimeFormat) => {
    if (typeof value === 'string' && CLOCK_TIME.test(value.trim())) return value.trim()
    const parsed = toDate(value)
    return parsed ? formatTime(parsed, format) : ''
}

const DateTime: FC<DateTimeProps> = ({
    timestamp,
    date,
    time,
    hideTimeOnSm = false,
    hideTimeOnMd = false,
    separator = ' - ',
    className,
}) => {
    const { dateFormat, timeFormat } = useDateFormat()
    const hasTimestamp = timestamp != null && timestamp !== ''
    const dateValue = hasTimestamp ? timestamp : date
    const timeValue = hasTimestamp ? timestamp : time
    const dateText = dateValue == null || dateValue === '' ? '' : dateTextFrom(dateValue, dateFormat)
    const timeText = timeValue == null || timeValue === '' ? '' : timeTextFrom(timeValue, timeFormat)

    if (!dateText && !timeText) return null

    return (
        <span className={className}>
            {dateText}
            {timeText ? (
                <span className={timeVisibilityClass(hideTimeOnSm, hideTimeOnMd)}>
                    {dateText ? separator : ''}
                    {timeText}
                </span>
            ) : null}
        </span>
    )
}

export default DateTime
